import {
  Component,
  OnInit,
  signal,
  inject,
  OnDestroy,
  computed,
  ChangeDetectorRef,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, debounceTime, Observable, of, forkJoin } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { GoodsStockResVO } from '@app/api/stock-service';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

import {
  AddFormComponent,
  FormField,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import {
  POR_ADD_FORM_FIELDS,
  POR_DETAIL_TABLE_SECTION,
  PurchaseReturnOrderDetailItem,
  AvailablePO,
} from '../purchase-return-order.config';
import { PurchaseReturnOrderService } from '../purchase-return-order.service';
import { ProcurementDropdownService } from '../../services/procurement-dropdown.service';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { VendorOptionsService } from '@shared/services/vendor-options.service';
import { withPaginatedFormFields } from '@shared/utils/paginated-field.helpers';
import {
  calculateReturnAmount,
  convertTaxAmountToRate,
  validateItemsForSave,
  updateFormFieldOptions,
  mapPOItemsToDetailItemsByWarehouse,
  deduplicatePOs,
  getMaxReturnQty,
} from '../por.helper';
import { 
  PORStockSelectionDialogComponent,
  PORStockSelectionItem 
} from './components/por-stock-selection-dialog/por-stock-selection-dialog.component';

@Component({
  selector: 'app-purchase-return-order-add',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AddFormComponent,
    ButtonModule,
    ToastModule,
    PORStockSelectionDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './purchase-return-order-add.component.html',
  styleUrl: './purchase-return-order-add.component.css',
})
export class PurchaseReturnOrderAddComponent implements OnInit, OnDestroy, AfterViewInit, CanComponentDeactivate {
  @ViewChild('addForm') addFormComponent?: AddFormComponent;
  
  private router = inject(Router);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private porService = inject(PurchaseReturnOrderService);
  private procurementDropdownService = inject(ProcurementDropdownService);
  private warehouseOptionsService = inject(WarehouseOptionsService);
  private vendorOptionsService = inject(VendorOptionsService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  formFields = signal(
    withPaginatedFormFields(POR_ADD_FORM_FIELDS, {
      poId: {
        loadPage: (params) =>
          this.procurementDropdownService.getDeliveredPurchaseOrderOptionsPage(
            params,
          ),
      },
      vendorId: {
        loadPage: (params) =>
          this.vendorOptionsService.getOptionsPage(params, { valueKey: 'id' }),
      },
      warehouseId: {
        loadPage: (params) =>
          this.warehouseOptionsService.getOptionsPage(params, {
            valueKey: 'code',
            status: true,
          }),
      },
    }),
  );
  
  headerActions = signal<ActionButton[]>([
    {
      label: 'common.actions.save',
      severity: 'info',
      action: 'save',
      disabled: false,
      loading: false,
    },
    {
      label: 'common.actions.cancel',
      severity: 'secondary',
      action: 'cancel',
      outlined: true,
      disabled: false,
      loading: false,
    },
  ]);

  // Data
  detailList = signal<PurchaseReturnOrderDetailItem[]>([]);
  selectedDetails = signal<PurchaseReturnOrderDetailItem[]>([]);
  formLoading = signal(false);
  loading = computed(() => this.formLoading());
  currentFormData: any = null;
  private skipCanDeactivate = false;

  // Options
  vendorOptions = signal<any[]>([]);
  warehouseOptions = signal<any[]>([]);
  poOptions = signal<AvailablePO[]>([]);

  // Selected values
  selectedVendorId = signal<number | null>(null);
  selectedWarehouseId = signal<string | null>(null); // Changed to string (code) instead of number (id)
  selectedPOId = signal<number | null>(null);
  
  // Store PO details for filtering items by warehouse
  poDetails = signal<any>(null);
  
  // Store stock data loaded from API (key: skuCode, value: stock data by warehouse)
  stockDataMap = signal<Map<string, Map<string, GoodsStockResVO & { availableQuantity: number }>>>(new Map());
  
  // Store mapped detail items by warehouseo we can filter by warehouse without calling API again
  detailItemsByWarehouse = signal<Map<string, PurchaseReturnOrderDetailItem[]>>(new Map());

  // Store vendor SKU codes (loaded when vendor is selected)
  vendorSkuCodes = signal<string[]>([]);
  // Map skuCode -> { vendorSkuCode, vendorSkuName } for enriching detail items
  vendorSkuMap = signal<Map<string, { vendorSkuCode: string; vendorSkuName: string }>>(
    new Map()
  );

  // Computed table sections - always show actions, but disable addItem when PO is selected
  tableSections = computed(() => {
    const hasSelection = this.selectedDetails().length > 0;
    const hasWarehouse = !!this.selectedWarehouseId();
    const hasPO = !!this.selectedPOId();
    const hasVendor = !!this.selectedVendorId(); // Check if vendor is selected
    
    // Always show actions, but control disabled state
    const actions = POR_DETAIL_TABLE_SECTION.actions?.map(action => {
      // Disable deleteSelected button when no items are selected
      if (action.action === 'deleteSelected') {
        return {
          ...action,
          disabled: !hasSelection,
        };
      }
      // Disable addItem button when:
      // 1. PO is selected (cannot add items when PO is selected)
      // 2. No warehouse selected
      // 3. No vendor selected (need vendor to filter SKUs)
      if (action.action === 'addItem') {
        return {
          ...action,
          disabled: hasPO || !hasWarehouse || !hasVendor,
        };
      }
      return action;
    });
    
    // Update columns based on PO selection
    // When PO is selected: price and taxAmount are read-only (currency)
    // When PO is NOT selected: price and taxAmount are editable (inputNumber)
    const updatedColumns = POR_DETAIL_TABLE_SECTION.columns?.map(col => {
      if (col.prop === 'price') {
        return {
          ...col,
          type: (hasPO ? 'currency' : 'inputNumber') as 'currency' | 'inputNumber',
          editable: !hasPO, // Editable only when no PO
          min: hasPO ? undefined : 0, // Min 0 for input
        };
      }
      if (col.prop === 'taxAmount') {
        return {
          ...col,
          type: (hasPO ? 'currency' : 'inputNumber') as 'currency' | 'inputNumber',
          editable: !hasPO, // Editable only when no PO
          min: hasPO ? undefined : 0, // Min 0 for input
          max: hasPO ? undefined : 100, // Max 100 for tax percentage
        };
      }
      return col;
    });
    
    const section = {
      ...POR_DETAIL_TABLE_SECTION,
      columns: updatedColumns,
      data: this.detailList(),
      selection: this.selectedDetails(),
      // Always show actions
      actions: actions,
    };
    return [section];
  });

  // Stock selection dialog
  showStockDialog = signal(false);
  dialogWarehouseCode = signal<string>('');
  selectedWarehouseCode = computed(() => {
    // selectedWarehouseId now stores code directly
    return this.selectedWarehouseId() || '';
  });
  existingSkuCodes = computed(() => {
    return this.detailList().map(item => item.skuCode);
  });

  // Cell value change debounce
  private cellValueChange$ = new Subject<{
    id: string;
    field: string;
    value: any;
  }>();

  ngOnInit(): void {
    this.initializeForm();
    this.preloadLookupOptions();
    this.loadAvailablePOs();

    // Debounced cell value changes for return qty validation
    this.cellValueChange$
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((event) => {
        this.onCellValueChange(event);
      });
  }

  ngAfterViewInit(): void {
    // ViewChild is available after view init
    // Subscribe to reason field changes directly for auto-fill
    setTimeout(() => {
      if (this.addFormComponent) {
        const form = (this.addFormComponent as any).form;
        if (form && form.get('reason')) {
          const reasonControl = form.get('reason');
          reasonControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((reason: string) => {
              // Auto-fill reason to all items when reason changes
              if (this.detailList().length > 0 || this.detailItemsByWarehouse().size > 0) {
                this.autoFillReasonToAllRecords(reason || '');
              }
            });
        }
      }
    }, 500); // Wait for form to be fully initialized
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.currentFormData = {
      vendorId: null,
      warehouseId: null,
      poId: null,
      reason: '',
    };
  }

  private preloadLookupOptions(): void {
    forkJoin({
      vendors: this.loadAllVendorLookupOptions(1, []),
      warehouses: this.loadAllWarehouseLookupOptions(1, []),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ vendors, warehouses }) => {
          this.vendorOptions.set(vendors);
          this.warehouseOptions.set(warehouses);
        },
      });
  }

  private loadAllVendorLookupOptions(
    page: number,
    acc: Array<{ label: string; value: number; code?: string; name: string }>,
  ): Observable<Array<{ label: string; value: number; code?: string; name: string }>> {
    return this.vendorOptionsService
      .getOptionsPage({ page, size: 100, searchText: '' }, { valueKey: 'id' })
      .pipe(
        switchMap((res) => {
          const merged = [
            ...acc,
            ...res.options.map((o) => ({
              label: o.label,
              value: Number(o.value),
              code: this.vendorOptionsService.getVendorById(Number(o.value))
                ?.code,
              name: o.label,
            })),
          ];
          const hasMore =
            typeof res.totalElements === 'number'
              ? merged.length < res.totalElements
              : res.pageContentLength === 100;
          if (!hasMore || res.pageContentLength === 0) {
            return of(merged);
          }
          return this.loadAllVendorLookupOptions(page + 1, merged);
        }),
      );
  }

  private loadAllWarehouseLookupOptions(
    page: number,
    acc: Array<{ label: string; value: string; code: string; name: string; id?: number }>,
  ): Observable<
    Array<{ label: string; value: string; code: string; name: string; id?: number }>
  > {
    return this.warehouseOptionsService
      .getOptionsPage(
        { page, size: 100, searchText: '' },
        { valueKey: 'code', status: true },
      )
      .pipe(
        switchMap((res) => {
          const merged = [
            ...acc,
            ...res.options.map((o) => {
              const code = String(o.value ?? '').trim();
              const warehouse = this.warehouseOptionsService.getWarehouseByCode(code);
              return {
                label: o.label,
                // POR warehouse dropdown uses warehouse code as the form value (valueKey: 'code').
                value: code,
                code: warehouse?.code || code || this.warehouseCodeFromLabel(o.label),
                name: warehouse?.name || o.label,
                id: warehouse?.id,
              };
            }),
          ];
          const hasMore =
            res.hasNextPage === true ||
            (typeof res.totalElements === 'number'
              ? merged.length < res.totalElements
              : res.pageContentLength === 100);
          if (!hasMore || res.pageContentLength === 0) {
            return of(merged);
          }
          return this.loadAllWarehouseLookupOptions(page + 1, merged);
        }),
      );
  }

  private warehouseCodeFromLabel(label: string | undefined): string {
    if (!label) {
      return '';
    }
    const separator = label.indexOf(' - ');
    return separator > 0 ? label.slice(0, separator) : '';
  }

  // Handle form value changes
  onFormValueChanges(formData: any): void {
    // Check if PO ID changed - use selectedPOId to avoid race condition
    const previousPOId = this.selectedPOId();
    const newPOId = formData?.poId;

    // If PO ID changed and has a value, load PO details
    // Only trigger if PO ID actually changed and is different from current
    if (newPOId && newPOId !== previousPOId) {
      // Skip if we're currently filling data to prevent duplicate triggers
      if (this.isFillingData) {
        return;
      }
      
      // Check if we're already loading this PO to avoid duplicate calls
      if (this.isLoadingPODetails) {
        return;
      }
      
      this.onPOSelected(newPOId);
    } else if (!newPOId && previousPOId) {
      // PO was cleared - enable vendor and warehouse, clear data
      this.onPOCleared();
    }

    // Check if warehouse changed
    const previousWarehouseId = this.currentFormData?.warehouseId;
    const newWarehouseId = formData?.warehouseId;

    // If PO is not selected, allow warehouse selection
    if (newWarehouseId && newWarehouseId !== previousWarehouseId && !this.selectedPOId()) {
      this.onWarehouseSelected(newWarehouseId);
    } else if (newWarehouseId && this.selectedPOId()) {
      // If PO is selected and warehouse is selected, only filter SKU if warehouse actually changed
      const warehouseChanged = newWarehouseId !== previousWarehouseId;
      
      if (!warehouseChanged) {
        // Warehouse didn't change, don't do anything
        return;
      }
      
      // Update selectedWarehouseId and currentFormData BEFORE loading SKU
      // newWarehouseId is now code (string)
      this.selectedWarehouseId.set(newWarehouseId);
      this.updateFormDataWithWarehouse(newWarehouseId);
      
      // Also update form control directly to ensure it shows the selected value
      requestAnimationFrame(() => {
        if (this.addFormComponent) {
          const form = (this.addFormComponent as any).form;
          if (form && form.get('warehouseId')) {
            const control = form.get('warehouseId');
            control.setValue(newWarehouseId, { emitEvent: false });
          }
        }
      });
      
      // Only load SKU when warehouse actually changed (stock data already loaded when PO was selected)
      // Use setTimeout to ensure selectedWarehouseId signal is updated before loading
      if (this.poDetails()) {
        setTimeout(() => {
          // Load SKU from already loaded PO details and stock data (no API call needed)
          // Filter items by the newly selected warehouse
          this.loadSKUFromPODetails(this.poDetails());
        }, 0);
      }
    }

    // Check if vendor changed
    const previousVendorId = this.currentFormData?.vendorId;
    const newVendorId = formData?.vendorId;
    if (newVendorId && newVendorId !== previousVendorId) {
      this.onVendorSelected(newVendorId);
    } else if (!newVendorId && previousVendorId) {
      this.vendorSkuCodes.set([]);
      this.vendorSkuMap.set(new Map());
    }

    // Check if reason changed - auto fill to all records (OVERRIDE all item reasons)
    const previousReason = this.currentFormData?.reason;
    const newReason = formData?.reason;
    // Auto-fill reason to all records when reason changes (even if empty string)
    // This will OVERRIDE any individual item reasons
    if (newReason !== undefined && newReason !== previousReason) {
      // Always auto-fill if there are items in the table (current or mapped)
      const hasCurrentItems = this.detailList().length > 0;
      const hasMappedItems = this.detailItemsByWarehouse().size > 0;
      
      if (hasCurrentItems || hasMappedItems) {
        this.autoFillReasonToAllRecords(newReason || '');
      }
    }

    // Update currentFormData
    this.currentFormData = formData;
    
    // Sync signals with currentFormData to ensure onSave() can access them
    if (formData?.vendorId !== undefined) {
      this.selectedVendorId.set(formData.vendorId);
    }
    if (formData?.warehouseId !== undefined) {
      this.selectedWarehouseId.set(formData.warehouseId);
    }
    if (formData?.poId !== undefined) {
      this.selectedPOId.set(formData.poId);
    }
  }

  // Handle PO cleared - enable vendor and warehouse, clear data
  private onPOCleared(): void {
    this.selectedPOId.set(null);
    this.detailList.set([]);
    this.poDetails.set(null);
    this.isLoadingPODetails = false;
    
    // Enable vendor and warehouse fields
    this.enableVendorAndWarehouseFields();
    
    // Clear vendor and warehouse from form data
    this.currentFormData = {
      ...this.currentFormData,
      poId: null,
      vendorId: null,
      warehouseId: null,
    };
    
    // Clear form control values
    requestAnimationFrame(() => {
      if (this.addFormComponent) {
        const form = (this.addFormComponent as any).form;
        if (form) {
          if (form.get('vendorId')) {
            form.get('vendorId').setValue(null, { emitEvent: false });
          }
          if (form.get('warehouseId')) {
            form.get('warehouseId').setValue(null, { emitEvent: false });
          }
        }
      }
    });
  }

  // Enable vendor and warehouse fields
  private enableVendorAndWarehouseFields(): void {
    const updatedFormFields = this.formFields().map((field) => {
      if (
        field.controlName === 'vendorId' ||
        field.controlName === 'warehouseId'
      ) {
        return { ...field, disabled: false };
      }
      return field;
    });
    this.formFields.set(updatedFormFields);
    
    // Enable form controls
    requestAnimationFrame(() => {
      if (this.addFormComponent) {
        const form = (this.addFormComponent as any).form;
        if (form) {
          if (form.get('vendorId')) {
            form.get('vendorId').enable({ emitEvent: false });
          }
          if (form.get('warehouseId')) {
            form.get('warehouseId').enable({ emitEvent: false });
          }
        }
      }
    });
  }

  // Step 1: Vendor selected
  private onVendorSelected(vendorId: number): void {
    this.selectedVendorId.set(vendorId);
    // Clear PO selection and SKU (but keep warehouse if already selected)
    this.selectedPOId.set(null);
    this.detailList.set([]);
    this.vendorSkuCodes.set([]); // Clear vendor SKU codes

    // Load vendor SKUs
    this.loadVendorSkus(vendorId);

    // Keep all PO options available - don't filter by vendor
    // Refresh PO options to ensure they're up to date
    if (this.allPOs().length > 0) {
      // Keep all POs - don't filter
      this.poOptions.set([...this.allPOs()]);
      // Update form fields with all PO options
      setTimeout(() => {
      }, 0);
    }

    // Update form data
    this.currentFormData = {
      ...this.currentFormData,
      vendorId: vendorId,
      poId: null,
    };
  }

  private loadVendorSkus(vendorId: number): void {
    this.formLoading.set(true);
    this.vendorSkuMap.set(new Map());

    this.porService
      .loadVendorSkuList(vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          this.buildAndSetVendorSkuMap(list);
          this.formLoading.set(false);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_return_order.messages.load_vendor_sku_error'
            ),
          });
          this.formLoading.set(false);
        },
      });
  }

  private buildAndSetVendorSkuMap(
    list: Array<{ skuCode: string; vendorSkuCode: string; vendorProductName: string }>
  ): void {
    const { map, skuCodes } = this.porService.buildVendorSkuMapAndCodes(list);
    this.vendorSkuMap.set(map);
    this.vendorSkuCodes.set(skuCodes);
  }

  // Step 2: Warehouse selected - filter PO by warehouse and items
  // warehouseCode is now the value (string), not id (number)
  private onWarehouseSelected(warehouseCode: string): void {
    this.selectedWarehouseId.set(warehouseCode);
    
    // Find warehouse info from options (value is now code)
    const warehouse = this.warehouseOptions().find(w => w.value === warehouseCode);
    
    // If PO is already selected, only filter items by warehouse (don't filter PO options)
    if (this.selectedPOId() && this.poDetails()) {
      // Update currentFormData with warehouse info
      this.updateFormDataWithWarehouse(warehouseCode);
      this.loadSKUFromPODetails(this.poDetails());
      // Don't filter PO options - keep all POs available for user to change selection
      return;
    }
    
    // If no PO selected, clear SKU but keep all PO options available
    this.selectedPOId.set(null);
    this.detailList.set([]);

    // Keep all PO options available - don't filter by warehouse or vendor
    if (this.allPOs().length > 0) {
      // Keep all POs - don't filter
      this.poOptions.set([...this.allPOs()]);
      // Update form fields with all PO options
      setTimeout(() => {
      }, 0);
    }

    // Update form data with warehouse info
    this.updateFormDataWithWarehouse(warehouseCode);
    this.currentFormData = {
      ...this.currentFormData,
      poId: null,
    };
    
    // If vendor is selected and has vendor SKU codes, stock will be loaded when user opens "Add Item" dialog
    // The dialog will filter by vendorSkuCodes and warehouseCode automatically
  }

  // All POs loaded (for filtering)
  allPOs = signal<AvailablePO[]>([]);
  
  // Flag to prevent multiple simultaneous loads
  private isLoadingPOs = false;

  // Load available PO - load all available POs and group by warehouse
  private loadAvailablePOs(): void {
    // Prevent multiple simultaneous loads
    if (this.isLoadingPOs) {
      return;
    }
    
    // If POs are already loaded, don't reload
    if (this.allPOs().length > 0) {
      return;
    }
    
    this.isLoadingPOs = true;
    this.formLoading.set(true);
    
    // Load all available POs with status=DELIVERED
    this.porService
      .getAvailablePOs()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pos) => {
          // Deduplicate POs based on id + warehouseCode
          const uniquePOs = deduplicatePOs(pos);
          this.allPOs.set(uniquePOs);
          // Initially show all POs (don't filter)
          this.poOptions.set(uniquePOs);
          // Update form fields with PO options
          setTimeout(() => {
          }, 0);
          this.isLoadingPOs = false;
          this.formLoading.set(false);
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('purchase_return_order.messages.load_po_error'),
          });
          this.isLoadingPOs = false;
          this.formLoading.set(false);
        },
      });
  }

  // Step 3: PO selected - get PO details and load SKU
  private onPOSelected(poId: number): void {
    // Check if this PO is already selected and loaded to avoid duplicate calls
    if (this.selectedPOId() === poId && this.poDetails()) {
      return;
    }

    // Find selected PO from allPOs (not poOptions, which might be filtered)
    const selectedPO = this.allPOs().find((po) => po.id === poId);
    if (!selectedPO) {
      return;
    }
    // Clear previous data first
    this.detailList.set([]);
    this.poDetails.set(null);
    this.selectedWarehouseId.set(null);
    this.stockDataMap.set(new Map());
    this.detailItemsByWarehouse.set(new Map());
    // Clear warehouse from form data to ensure change detection works
    this.currentFormData = {
      ...this.currentFormData,
      warehouseId: null,
    };

    this.selectedPOId.set(poId);
    this.loadPODetailsAndSKU(poId);
  }

  // Flag to prevent multiple simultaneous PO detail loads
  private isLoadingPODetails = false;
  
  // Flag to prevent form value changes from triggering during data fill
  private isFillingData = false;
  
  // Flag to prevent multiple simultaneous SKU loads
  private isLoadingSKU = false;

  // Load PO details and then SKU from GRN
  private loadPODetailsAndSKU(poId: number): void {
    // Prevent multiple simultaneous loads
    if (this.isLoadingPODetails) {
      return;
    }
    
    // Check if this PO is already loaded
    if (this.selectedPOId() === poId && this.poDetails()) {
      return;
    }
    
    this.isLoadingPODetails = true;
    this.formLoading.set(true);
    
    // Find PO from allPOs (not poOptions, which might be filtered)
    const selectedPO = this.allPOs().find((po) => po.id === poId);
    
    this.porService
      .getPODetails(poId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (poDetails) => {
          
          // Check if poDetails is still a Blob (shouldn't happen but just in case)
          if (poDetails instanceof Blob) {
            this.formLoading.set(false);
            return;
          }
          
          // Store PO details for filtering
          this.poDetails.set(poDetails);
          
          // Set flag to prevent form value changes from triggering during data fill
          this.isFillingData = true;
          
          try {
            // Update currentFormData.poId after API call succeeds
            this.currentFormData = {
              ...this.currentFormData,
              poId: poId,
            };
            
            // Fill vendor from PO details
            this.fillVendorFromPODetails(poDetails);
            
            // Load stock data then vendor SKU list (flattened with switchMap)
            this.loadStockDataForPOItems(poDetails)
              .pipe(
                switchMap(() => {
                  const vendorId = this.selectedVendorId();
                  return vendorId
                    ? this.porService.loadVendorSkuList(vendorId)
                    : of([]);
                }),
                takeUntil(this.destroy$)
              )
              .subscribe({
                next: (list) => {
                  this.buildAndSetVendorSkuMap(list);
                  this.applyEnrichAndSyncDetailList();
                  this.loadWarehouseOptionsFromPOItems(poDetails);
                },
                error: () => {
                  this.loadWarehouseOptionsFromPOItems(poDetails);
                },
              });
          } finally {
            setTimeout(() => {
              this.isFillingData = false;
            }, 200);
          }
          this.isLoadingPODetails = false;
          this.formLoading.set(false);
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_return_order.messages.load_po_details_error'
            ),
          });
          this.isLoadingPODetails = false;
          this.formLoading.set(false);
        },
      });
  }

  // Load warehouse options from PO items (use already loaded warehouse options)
  private loadWarehouseOptionsFromPOItems(poDetails: any): void {
    const items = poDetails.items || [];

    // Preserve first-seen order from PO lines — used as auto-select preference.
    const warehouseCodes = new Set<string>();
    items.forEach((item: any) => {
      if (item.warehouseCode) {
        warehouseCodes.add(String(item.warehouseCode).trim());
      }
    });

    if (warehouseCodes.size === 0) {
      return;
    }

    let allWarehouses = this.warehouseOptions();

    if (allWarehouses.length === 0) {
      this.loadAllWarehouseLookupOptions(1, [])
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (warehouses) => {
            this.warehouseOptions.set(warehouses);
            allWarehouses = warehouses;
            this.processWarehouseMatching(allWarehouses, warehouseCodes, poDetails);
          },
          error: () => {},
        });
      return;
    }

    this.processWarehouseMatching(allWarehouses, warehouseCodes, poDetails);
  }

  /** Always resolve warehouse form values to code — never numeric id. */
  private warehouseFormValue(warehouse: {
    code?: string;
    value?: string | number | null;
  } | null): string {
    const code = String(warehouse?.code ?? '').trim();
    if (code) {
      return code;
    }
    const raw = warehouse?.value;
    if (raw == null || raw === '') {
      return '';
    }
    if (typeof raw === 'number' || /^\d+$/.test(String(raw))) {
      const byId = this.warehouseOptionsService.getWarehouseById(Number(raw));
      if (byId?.code) {
        return byId.code;
      }
    }
    return String(raw).trim();
  }

  private processWarehouseMatching(
    allWarehouses: any[],
    warehouseCodes: Set<string>,
    poDetails: any
  ): void {
    const poWarehouseCodes = Array.from(warehouseCodes);
    const normalizedPoCodes = new Set(
      poWarehouseCodes.map((code) => code.toLowerCase()),
    );

    const matchedWarehouses = allWarehouses
      .filter((wh) => {
        const whCode = String(wh.code ?? '').trim();
        return whCode && normalizedPoCodes.has(whCode.toLowerCase());
      })
      .map((wh) => ({
        ...wh,
        code: String(wh.code).trim(),
        value: String(wh.code).trim(),
      }));

    poWarehouseCodes.forEach((code) => {
      const alreadyMatched = matchedWarehouses.some(
        (wh) => wh.code.toLowerCase() === code.toLowerCase(),
      );
      if (alreadyMatched) {
        return;
      }
      const cached = this.warehouseOptionsService.getWarehouseByCode(code);
      if (!cached?.code) {
        return;
      }
      matchedWarehouses.push({
        label: cached.name ? `${cached.code} - ${cached.name}` : cached.code,
        value: cached.code,
        code: cached.code,
        name: cached.name || cached.code,
        id: cached.id,
      });
    });

    // Prefer PO line order over master-list order when auto-selecting.
    const orderedMatched = poWarehouseCodes
      .map((code) =>
        matchedWarehouses.find(
          (wh) => wh.code.toLowerCase() === code.toLowerCase(),
        ),
      )
      .filter((wh): wh is (typeof matchedWarehouses)[0] => !!wh);

    if (orderedMatched.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'purchase_return_order.messages.no_warehouse_match',
        ),
      });

      const warehouseOptionsFromCodes = poWarehouseCodes.map((code) => ({
        label: code,
        value: code,
        code: code,
        name: code,
      }));

      this.updateWarehouseDropdown(warehouseOptionsFromCodes, null, poDetails);
      return;
    }

    let currentWarehouseValue: any = null;
    if (this.addFormComponent) {
      const form = (this.addFormComponent as any).form;
      if (form && form.get('warehouseId')) {
        currentWarehouseValue = form.get('warehouseId').value;
      }
    }
    if (!currentWarehouseValue && this.currentFormData?.warehouseId) {
      currentWarehouseValue = this.currentFormData.warehouseId;
    }
    const currentCode = this.warehouseFormValue({
      code:
        typeof currentWarehouseValue === 'string' &&
        !/^\d+$/.test(currentWarehouseValue)
          ? currentWarehouseValue
          : undefined,
      value: currentWarehouseValue,
    });

    let warehouseToSelect: (typeof orderedMatched)[0] | null = null;
    let shouldAutoSelect = false;

    if (!currentCode) {
      warehouseToSelect = orderedMatched[0];
      shouldAutoSelect = true;
    } else {
      const userSelectedWarehouse = orderedMatched.find(
        (wh) => wh.code.toLowerCase() === currentCode.toLowerCase(),
      );
      if (userSelectedWarehouse) {
        warehouseToSelect = userSelectedWarehouse;
      } else {
        warehouseToSelect = orderedMatched[0];
        shouldAutoSelect = true;
      }
    }

    const selectedCode = this.warehouseFormValue(warehouseToSelect);

    if (shouldAutoSelect && selectedCode) {
      this.selectedWarehouseId.set(selectedCode);
      this.currentFormData = {
        ...this.currentFormData,
        warehouseId: selectedCode,
      };
    } else if (warehouseToSelect && selectedCode) {
      this.selectedWarehouseId.set(selectedCode);
    }

    this.updateWarehouseDropdown(
      orderedMatched,
      warehouseToSelect,
      poDetails,
      shouldAutoSelect,
    );

    if (warehouseToSelect && this.poDetails() && shouldAutoSelect) {
      this.loadSKUFromPODetails(poDetails);
    }
  }

  private updateWarehouseDropdown(
    matchedWarehouses: any[],
    warehouseToSelect: any,
    poDetails: any,
    shouldAutoSelect: boolean = false
  ): void {
    // Get current warehouse value from form control to preserve user selection
    let currentWarehouseValue: any = null;
    if (this.addFormComponent) {
      const form = (this.addFormComponent as any).form;
      if (form && form.get('warehouseId')) {
        currentWarehouseValue = form.get('warehouseId').value;
      }
    }

    const currentCode = this.warehouseFormValue({
      code:
        typeof currentWarehouseValue === 'string' &&
        !/^\d+$/.test(currentWarehouseValue)
          ? currentWarehouseValue
          : undefined,
      value: currentWarehouseValue,
    });

    let finalWarehouseValue: string | null = null;
    let finalWarehouseLabel = '';

    if (shouldAutoSelect && warehouseToSelect) {
      finalWarehouseValue = this.warehouseFormValue(warehouseToSelect) || null;
      finalWarehouseLabel = warehouseToSelect.label || '';
    } else if (currentCode) {
      const userSelectedWarehouse = matchedWarehouses.find(
        (wh) =>
          this.warehouseFormValue(wh).toLowerCase() === currentCode.toLowerCase(),
      );
      if (userSelectedWarehouse) {
        finalWarehouseValue = currentCode;
        finalWarehouseLabel = userSelectedWarehouse.label || '';
      } else if (warehouseToSelect) {
        finalWarehouseValue = this.warehouseFormValue(warehouseToSelect) || null;
        finalWarehouseLabel = warehouseToSelect.label || '';
      }
    } else if (warehouseToSelect) {
      finalWarehouseValue = this.warehouseFormValue(warehouseToSelect) || null;
      finalWarehouseLabel = warehouseToSelect.label || '';
    }

    // Enable warehouse field and update options (codes only — matches valueKey: 'code')
    const updatedFormFields = this.formFields().map((field) => {
      if (field.controlName === 'warehouseId') {
        return {
          ...field,
          type: 'searchableDropdown' as const,
          disabled: false,
          options: matchedWarehouses.map((wh) => {
            const code = this.warehouseFormValue(wh);
            return {
              label: wh.label || code,
              value: code,
            };
          }),
          value: finalWarehouseValue,
          displayValue: finalWarehouseLabel,
        };
      }
      return field;
    });
    this.formFields.set(updatedFormFields);

    // Update form control to enable warehouse dropdown and set value
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.addFormComponent) {
          const form = (this.addFormComponent as any).form;
          if (form && form.get('warehouseId')) {
            const control = form.get('warehouseId');
            control.enable({ emitEvent: false });

            const currentValue = control.value;
            if (
              finalWarehouseValue !== null &&
              finalWarehouseValue !== currentValue
            ) {
              control.setValue(finalWarehouseValue, { emitEvent: false });

              if (shouldAutoSelect) {
                this.currentFormData = {
                  ...this.currentFormData,
                  warehouseId: finalWarehouseValue,
                };
              }
            }
            this.cdr.detectChanges();
          }
        }
        this.formLoading.set(false);
      }, 50);
    });
  }

  // Fill vendor from PO details into form and disable it
  private fillVendorFromPODetails(poDetails: any): void {
    
    // Get vendor info from PO details - use vendor.vendorName
    const vendorName = poDetails.vendor?.vendorName || '';
    const vendorCode = poDetails.vendorCode || poDetails.vendor?.vendorCode || '';
    
    
    if (!vendorName && !vendorCode) {
      return;
    }
    
    // Use vendor options already loaded (don't call API again)
    const vendors = this.vendorOptions();
    
    // Find vendor by code
    const vendor =
      vendors.find((v) => v.code === vendorCode) ||
      this.vendorOptionsService.getVendorByCode(vendorCode);

    const vendorId = vendor?.value || 0;
    
    
    if (vendorId) {
      this.selectedVendorId.set(vendorId);
    }
    
    // Update form fields with vendor - disable it and set value
    const updatedFormFields = this.formFields().map((field) => {
      if (field.controlName === 'vendorId') {
        return {
          ...field,
          type: 'searchableDropdown' as const, // Keep as dropdown but disabled
          disabled: true,
          value: vendorId || vendorCode,
          displayValue: vendorName || vendorCode || '',
        };
      }
      return field;
    });
    this.formFields.set(updatedFormFields);
    
    // Update form control values directly via add-form component
    // Use requestAnimationFrame to ensure form is ready and avoid flickering
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.addFormComponent) {
          const form = (this.addFormComponent as any).form;
          if (form && form.get('vendorId')) {
            // Set vendor ID as value
            form.get('vendorId').setValue(vendorId || vendorCode, { emitEvent: false });
            form.get('vendorId').disable({ emitEvent: false });
            this.cdr.detectChanges();
          }
        }
      }, 50);
    });
    
    // Update form data - store vendor ID
    this.currentFormData = {
      ...this.currentFormData,
      vendorId: vendorId || vendorCode,
      vendorCode,
      vendorName,
    };
  }

  private loadStockDataForPOItems(poDetails: any): Observable<void> {
    return this.porService.loadStockMapForPOItems(poDetails).pipe(
      map((finalStockMap) => {
        this.stockDataMap.set(finalStockMap);
        this.mapPOItemsWithStockData(poDetails, finalStockMap);
      }),
      catchError(() => of(undefined))
    );
  }

  private applyEnrichAndSyncDetailList(): void {
    const enriched = this.porService.enrichDetailItemsWithVendorSku(
      this.detailItemsByWarehouse(),
      this.vendorSkuMap()
    );
    this.detailItemsByWarehouse.set(enriched);
    const wh = this.selectedWarehouseId();
    if (wh) {
      this.detailList.set(enriched.get(wh) ?? []);
    }
  }

  private mapPOItemsWithStockData(
    poDetails: any,
    stockMap: Map<string, Map<string, GoodsStockResVO & { availableQuantity: number }>>
  ): void {
    const detailItemsMap = mapPOItemsToDetailItemsByWarehouse(
      poDetails,
      stockMap
    );
    this.detailItemsByWarehouse.set(detailItemsMap);

    const currentReason = this.currentFormData?.reason;
    if (currentReason) {
      const updatedDetailItemsMap = new Map<
        string,
        PurchaseReturnOrderDetailItem[]
      >();
      detailItemsMap.forEach((items, warehouseCode) => {
        const updatedItems = items.map((item) => ({
          ...item,
          reason: currentReason,
        }));
        updatedDetailItemsMap.set(warehouseCode, updatedItems);
      });
      this.detailItemsByWarehouse.set(updatedDetailItemsMap);

      const selectedWarehouseId = this.selectedWarehouseId();
      if (selectedWarehouseId) {
        const selectedWarehouse = this.warehouseOptions().find(
          (wh) => wh.value === selectedWarehouseId
        );
        if (selectedWarehouse) {
          const filteredItems =
            updatedDetailItemsMap.get(selectedWarehouse.code) || [];
          this.detailList.set(filteredItems);
        }
      }
    }
  }

  // Load SKU from PO details - filter by selected warehouse (no API call, just filter from mapped data)
  private loadSKUFromPODetails(poDetails: any): void {
    // Prevent multiple simultaneous loads
    if (this.isLoadingSKU) {
      return;
    }
    
    this.isLoadingSKU = true;
    
    const selectedWarehouseId = this.selectedWarehouseId();
    
    if (!selectedWarehouseId) {
      this.detailList.set([]);
      this.formLoading.set(false);
      this.isLoadingSKU = false;
      return;
    }

    // Find warehouse code from selected warehouse
    // Try both number and string comparison to handle type mismatches
    const selectedWarehouse = this.warehouseOptions().find(
      (wh) => wh.value === selectedWarehouseId || 
              Number(wh.value) === Number(selectedWarehouseId) ||
              String(wh.value) === String(selectedWarehouseId)
    );
    
    if (!selectedWarehouse) {
      this.detailList.set([]);
      this.formLoading.set(false);
      this.isLoadingSKU = false;
      return;
    }

    // Get mapped items by warehouse (already mapped when PO was selected)
    const detailItemsMap = this.detailItemsByWarehouse();
    const warehouseCode = selectedWarehouse.code;
    const filteredItems = detailItemsMap.get(warehouseCode) || [];


    // Set the filtered items to detail list
    this.detailList.set(filteredItems);
    this.formLoading.set(false);
    this.isLoadingSKU = false;
    
  }


  // Handle cell value change (for return qty validation)
  private onCellValueChange(event: {
    id: string;
    field: string;
    value: any;
  }): void {
    const item = this.detailList().find((item) => item.id === event.id);
    if (!item) return;

    if (event.field === 'returnQty') {
      const returnQty = Number(event.value) || 0;

        // Calculate return amount
        const returnAmount = calculateReturnAmount(returnQty, item.price || 0);

      // Update item - always update value, validation will be shown via getDetailFieldError
      const updatedList = this.detailList().map((i) =>
        i.id === event.id
          ? { ...i, returnQty: returnQty, returnAmount: returnAmount }
          : i
      );
      this.detailList.set(updatedList);
    } else if (event.field === 'price') {
      const price = Number(event.value) || 0;
      const returnQty = item.returnQty || 0;
      const returnAmount = calculateReturnAmount(returnQty, price);

      // Update price and recalculate return amount
      const updatedList = this.detailList().map((i) =>
        i.id === event.id
          ? { ...i, price: price, returnAmount: returnAmount }
          : i
      );
      this.detailList.set(updatedList);
    } else if (event.field === 'taxAmount') {
      const taxAmount = Number(event.value) || 0;
      const tax = convertTaxAmountToRate(taxAmount);

      // Update taxAmount and tax
      const updatedList = this.detailList().map((i) =>
        i.id === event.id
          ? { ...i, taxAmount: taxAmount, tax: tax }
          : i
      );
      this.detailList.set(updatedList);
    } else if (event.field === 'reason') {
      // Update reason for specific item
      const updatedList = this.detailList().map((i) =>
        i.id === event.id ? { ...i, reason: event.value || '' } : i
      );
      this.detailList.set(updatedList);
    }
  }

  // Auto fill reason from form to all records - OVERRIDE all items (both current and future)
  private autoFillReasonToAllRecords(reason: string): void {
    // Allow empty string to clear reason from all items
    const reasonValue = reason || '';
    
    // Update detailList (current displayed items) - OVERRIDE all reasons
    const currentList = this.detailList();
    const updatedList = currentList.map((item) => ({
      ...item,
      reason: reasonValue, // Override individual item reason
    }));
    this.detailList.set(updatedList);
    
    // Also update detailItemsByWarehouse (all mapped items by warehouse) - OVERRIDE all reasons
    const detailItemsMap = this.detailItemsByWarehouse();
    if (detailItemsMap.size > 0) {
      const updatedDetailItemsMap = new Map<string, PurchaseReturnOrderDetailItem[]>();
      detailItemsMap.forEach((items, warehouseCode) => {
        const updatedItems = items.map(item => ({
          ...item,
          reason: reasonValue, // Override individual item reason
        }));
        updatedDetailItemsMap.set(warehouseCode, updatedItems);
      });
      this.detailItemsByWarehouse.set(updatedDetailItemsMap);
    }
  }

  // Validate return qty - check against available quantity
  private validateReturnQty(
    item: PurchaseReturnOrderDetailItem,
    returnQty: number
  ): boolean {
    if (returnQty < 0) {
      return false;
    }
    return returnQty <= getMaxReturnQty(item);
  }

  // Get field error for detail items
  getDetailFieldError(rowIndex: number, field: string): string | null {
    if (field !== 'returnQty') return null;

    // Always get the latest data from signal
    const items = this.detailList();
    const item = items[rowIndex];
    if (!item) return null;

    const returnQty = item.returnQty ?? 0;

    // Validate: returnQty must be >= 0
    if (returnQty < 0) {
      return this.translateService.instant('purchase_return_order.validation.negative_qty') || 'Return quantity cannot be negative';
    }

    if (returnQty > getMaxReturnQty(item)) {
      return this.translateService.instant('purchase_return_order.validation.exceeds_available');
    }

    return null;
  }

  // Handle form actions
  onFormAction(action: string): void {
    switch (action) {
      case 'save':
        this.onSave();
        break;
      case 'cancel':
        this.onCancel();
        break;
    }
  }

  // Handle table actions
  onTableAction(event: { section?: string; action: string; data?: any }): void {
    switch (event.action) {
      case 'cellValueChange':
        // Convert format from { rowData, prop, value } to { id, field, value }
        if (event.data && event.data.rowData) {
          this.cellValueChange$.next({
            id: event.data.rowData.id,
            field: event.data.prop,
            value: event.data.value,
          });
        }
        break;
      case 'addItem':
        this.onAddItem();
        break;
      case 'deleteItem':
        this.onDeleteItem(event.data);
        break;
      case 'deleteSelected':
        this.onDeleteSelected(event.data);
        break;
      case 'selectionChange':
        this.onSelectionChange(event.data);
        break;
    }
  }

  // Add item from stock selection dialog
  private onAddItem(): void {
    // Button is already enabled so warehouse should be selected
    // selectedWarehouseId now stores code (string) directly
    const warehouseCode = this.selectedWarehouseId();
    if (!warehouseCode) {
      return;
    }
    
    // Set warehouse code directly (it's already the code)
    this.dialogWarehouseCode.set(warehouseCode);
    this.showStockDialog.set(true);
  }

  // Delete item from detail list
  onDeleteItem(item: PurchaseReturnOrderDetailItem): void {
    const updatedList = this.detailList().filter((i) => i.id !== item.id);
    this.detailList.set(updatedList);
    // Also remove from selection if it was selected
    const updatedSelection = this.selectedDetails().filter((i) => i.id !== item.id);
    this.selectedDetails.set(updatedSelection);
  }

  // Handle selection change
  private onSelectionChange(selected: PurchaseReturnOrderDetailItem[]): void {
    this.selectedDetails.set(selected);
  }

  // Delete selected items
  private onDeleteSelected(selectedItems: PurchaseReturnOrderDetailItem[]): void {
    if (!selectedItems || selectedItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant('purchase_return_order.messages.no_items_selected'),
      });
      return;
    }

    // Remove selected items from detail list
    const selectedIds = selectedItems.map((item) => item.id);
    const updatedList = this.detailList().filter(
      (i) => !selectedIds.includes(i.id)
    );
    this.detailList.set(updatedList);
    
    // Clear selection
    this.selectedDetails.set([]);

  }

  // Handle stock selection dialog confirm
  onStockDialogConfirm(selectedItems: PORStockSelectionItem[]): void {
    const currentReason = this.currentFormData?.reason || '';
    const vMap = this.vendorSkuMap();

    const newItems: PurchaseReturnOrderDetailItem[] = selectedItems.map(
      (item, index) => {
        const existingItem = this.detailList().find(
          (i) => i.skuCode === item.skuCode
        );
        if (existingItem) return existingItem;

        const v = vMap.get(item.skuCode ?? '');
        return {
          id: `item-${Date.now()}-${index}`,
          grnDetailId: 0,
          skuCode: item.skuCode || '',
          skuName: item.skuName || '',
          vendorSkuCode: v?.vendorSkuCode ?? (item as any).vendorSkuCode ?? '',
          vendorSkuName: v?.vendorSkuName ?? (item as any).vendorSkuName ?? '',
          spuCode: item.spuCode || '',
          spuName: item.spuName || '',
          uom: '',
          qtyReceived: 0,
          qtyReturnedBefore: 0,
          qtyAvailable: item.availableQuantity || 0,
          currentStock: item.stockNum || 0,
          returnQty: 0,
          price: 0,
          returnAmount: 0,
          taxAmount: 0,
          reason: currentReason,
        };
      }
    );

    // Filter out duplicates and add new items
    const existingSkuCodes = this.detailList().map(i => i.skuCode);
    const uniqueNewItems = newItems.filter(item => !existingSkuCodes.includes(item.skuCode));
    
    this.detailList.set([...this.detailList(), ...uniqueNewItems]);
    this.showStockDialog.set(false);
  }

  // Save
  private onSave(): void {
    const form = (this.addFormComponent as any)?.form;
    const formWarehouseId = this.unwrapOptionValue(form?.get('warehouseId')?.value);
    const formVendorId = this.unwrapOptionValue(form?.get('vendorId')?.value);
    const formReason = form?.get('reason')?.value;
    const formPorNo = form?.get('porNo')?.value;

    const vendorId = formVendorId ?? this.currentFormData?.vendorId ?? this.selectedVendorId();
    const warehouseRaw =
      formWarehouseId ??
      this.currentFormData?.warehouseId ??
      this.currentFormData?.warehouseCode ??
      this.selectedWarehouseId();
    const porNo = String(formPorNo || this.currentFormData?.porNo || '').trim();

    const selectedVendor = this.findVendorById(vendorId);
    const selectedWarehouse = this.findWarehouseForSave(warehouseRaw);

    if (!selectedVendor || !selectedWarehouse) {
      this.showError('purchase_return_order.messages.missing_required_fields');
      return;
    }

    const warehouseIdNum =
      this.resolveWarehouseId(warehouseRaw, selectedWarehouse);

    const vendorIdNum = Number(this.unwrapOptionValue(vendorId));
    const reason = String(formReason || this.currentFormData?.reason || '').trim();
    const itemsWithReturnQty = this.detailList().filter((item) => (item.returnQty || 0) > 0);
    const poId = this.selectedPOId();
    const selectedPO = poId ? this.poOptions().find((po) => po.id === poId) : null;

    this.continueSave(
      Number.isFinite(vendorIdNum) ? vendorIdNum : 0,
      warehouseIdNum ?? 0,
      selectedWarehouse,
      selectedVendor,
      reason,
      itemsWithReturnQty,
      poId,
      selectedPO,
      porNo,
    );
  }

  // Continue save after validation
  private continueSave(
    vendorIdNum: number,
    warehouseIdNum: number,
    selectedWarehouse: any,
    selectedVendor: any,
    reason: string,
    itemsWithReturnQty: PurchaseReturnOrderDetailItem[],
    poId: number | null,
    selectedPO: any,
    porNo: string = ''  
  ): void {
    // Validate reason
    if (!reason) {
      this.showError('common.validation.required', {
        field: this.translateService.instant('purchase_return_order.add.fields.reason'),
      });
      return;
    }

    // RBA-4905: PO number is required — validate before item qty so the message is clear.
    if (!poId) {
      this.showError('purchase_return_order.messages.po_number_required');
      return;
    }

    // Validate items using helper
    const itemsValidation = validateItemsForSave(itemsWithReturnQty, !!poId);
    if (!itemsValidation.isValid) {
      if (itemsValidation.field) {
        this.showError(itemsValidation.errorKey!, {
          field: this.translateService.instant(itemsValidation.field),
        });
      } else {
        this.showError(itemsValidation.errorKey!);
      }
      return;
    }

    let payload;
    try {
      payload = this.porService.buildCreatePayload(
        vendorIdNum!,
        warehouseIdNum!,
        { code: selectedWarehouse.code, name: selectedWarehouse.name },
        { code: selectedVendor.code, name: selectedVendor.name },
        reason,
        itemsWithReturnQty,
        poId,
        selectedPO,
        porNo
      );
    } catch {
      this.showError('purchase_return_order.messages.create_error');
      return;
    }

    // Update headerActions to show loading state on save button
    this.headerActions.update((actions) =>
      actions.map((action) =>
        action.action === 'save'
          ? { ...action, loading: true, disabled: true }
          : action
      )
    );

    let create$;
    try {
      create$ = this.porService.createPOR(payload);
    } catch {
      this.headerActions.update((actions) =>
        actions.map((action) =>
          action.action === 'save'
            ? { ...action, loading: false, disabled: false }
            : action
        )
      );
      this.showError('purchase_return_order.messages.create_error');
      return;
    }

    create$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Reset save button loading state
          this.headerActions.update((actions) =>
            actions.map((action) =>
              action.action === 'save'
                ? { ...action, loading: false, disabled: false }
                : action
            )
          );
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant('purchase_return_order.messages.create_success'),
          });
          this.router.navigate(['/procurement/purchase-return-order']);
        },
        error: (error) => {
          // Reset save button loading state
          this.headerActions.update((actions) =>
            actions.map((action) =>
              action.action === 'save'
                ? { ...action, loading: false, disabled: false }
                : action
            )
          );
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('purchase_return_order.messages.create_error'),
          });
        },
      });
  }

  // Cancel
  private onCancel(): void {
    this.router.navigate(['/procurement/purchase-return-order']);
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.formLoading()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  private unwrapOptionValue(value: unknown): unknown {
    if (value && typeof value === 'object' && 'value' in (value as object)) {
      return (value as { value: unknown }).value;
    }
    return value;
  }

  // Warehouse Helpers
  private findWarehouseByCode(code: string): any {
    if (!code) {
      return undefined;
    }
    return this.warehouseOptions().find(
      (w) =>
        w.code === code ||
        w.value === code ||
        String(w.code) === String(code) ||
        String(w.value) === String(code),
    );
  }

  private updateFormDataWithWarehouse(warehouseCode: string): void {
    const warehouse = this.findWarehouseByCode(warehouseCode);
    this.currentFormData = {
      ...this.currentFormData,
      warehouseId: warehouseCode,
      warehouseCode: warehouse?.code || warehouseCode,
      warehouseName: warehouse?.name || warehouse?.label || '',
    };
  }

  private createWarehouseFromCode(code: string, name?: string): any {
    return {
      value: code,
      code: code,
      name: name || code,
      label: name || code,
    };
  }

  // Vendor Helpers
  private findVendorById(vendorId: number | string): any {
    const unwrapped = this.unwrapOptionValue(vendorId) as number | string;
    const vendorIdNum = unwrapped !== null && unwrapped !== undefined && unwrapped !== ''
      ? Number(unwrapped)
      : null;

    const fromOptions = this.vendorOptions().find((v) => {
      return (
        v.value === vendorIdNum ||
        v.value === unwrapped ||
        Number(v.value) === vendorIdNum ||
        v.code === unwrapped ||
        v.code === String(unwrapped)
      );
    });
    if (fromOptions) {
      return fromOptions;
    }

    if (vendorIdNum && !Number.isNaN(vendorIdNum)) {
      const cached = this.vendorOptionsService.getVendorById(vendorIdNum);
      if (cached) {
        return cached;
      }
    }

    if (typeof unwrapped === 'string' && unwrapped) {
      const byCode = this.vendorOptionsService.getVendorByCode(unwrapped);
      if (byCode) {
        return byCode;
      }
    }

    if (this.currentFormData?.vendorCode) {
      return {
        value: vendorIdNum || unwrapped,
        code: this.currentFormData.vendorCode,
        name: this.currentFormData.vendorName || this.currentFormData.vendorCode,
      };
    }

    return undefined;
  }

  private resolveWarehouseId(warehouseRaw: unknown, selectedWarehouse: any): number | null {
    const unwrapped = this.unwrapOptionValue(warehouseRaw);
    const asNumber = Number(unwrapped);
    if (Number.isFinite(asNumber) && asNumber > 0) {
      return (
        this.warehouseOptionsService.getWarehouseById(asNumber)?.id ??
        selectedWarehouse?.id ??
        asNumber
      );
    }

    const code = String(unwrapped ?? selectedWarehouse?.code ?? '').trim();
    return (
      this.warehouseOptionsService.getWarehouseByCode(code)?.id ??
      selectedWarehouse?.id ??
      null
    );
  }

  private findWarehouseForSave(warehouseCode: string): any {
    const code = String(this.unwrapOptionValue(warehouseCode) ?? '').trim();
    if (!code) {
      return undefined;
    }

    // Try to find from warehouseOptions signal
    let selectedWarehouse = this.findWarehouseByCode(code);

    // Paginated warehouse dropdown caches the full warehouse by code/id
    if (!selectedWarehouse) {
      selectedWarehouse =
        this.warehouseOptionsService.getWarehouseByCode(code) ??
        (Number.isFinite(Number(code))
          ? this.warehouseOptionsService.getWarehouseById(Number(code))
          : undefined);
    }
    
    // If not found, try to get from form fields
    if (!selectedWarehouse && this.addFormComponent) {
      const warehouseField = this.formFields().find(f => f.controlName === 'warehouseId');
      if (warehouseField?.options) {
        selectedWarehouse = warehouseField.options.find(
          (w: any) => w.value === code || w.code === code,
        );
      }
    }
    
    // If still not found, create from currentFormData
    if (!selectedWarehouse) {
      const warehouseName = this.currentFormData?.warehouseName;
      selectedWarehouse = this.createWarehouseFromCode(code, warehouseName);
    }
    
    // Ensure warehouseCode exists
    if (selectedWarehouse && !selectedWarehouse.code) {
      selectedWarehouse.code = selectedWarehouse.value || code;
    }
    
    return selectedWarehouse;
  }

  // Error Display Helper
  private showError(messageKey: string, params?: any): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: this.translateService.instant(messageKey, params),
    });
  }
}

